// Web client for the LibraryHub relay. Connects over a WebSocket (cookie auth,
// same origin), tracks which of your devices are online and what books they
// offer ("shadow library"), and downloads a chosen book by streaming it
// through the hub from the device that has it.

export interface HubBook {
  id: string;
  title: string;
  type: 'PDF' | 'EPUB';
  size?: number;
}
export interface HubDevice {
  deviceId: string;
  deviceName: string;
  books: HubBook[];
}

type PresenceCb = (devices: HubDevice[]) => void;

interface Pending {
  onBegin?: (m: { size?: number; mime?: string; name?: string }) => void;
  onChunk: (bytes: Uint8Array, last: boolean) => void;
  onError: (message: string) => void;
}

function wsBase(): string {
  const apiBase = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');
  const http = apiBase || location.origin;
  return http.replace(/^http/, 'ws');
}

function getDeviceId(): string {
  const KEY = 'scrollwind-device-id';
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(KEY, id);
  }
  return id;
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

let socket: WebSocket | null = null;
let presence: HubDevice[] = [];
let closedByUs = false;
const presenceSubs = new Set<PresenceCb>();
const pending = new Map<string, Pending>();

function emitPresence() {
  for (const cb of presenceSubs) cb(presence);
}

export function onPresence(cb: PresenceCb): () => void {
  presenceSubs.add(cb);
  cb(presence);
  return () => presenceSubs.delete(cb);
}

export function connectHub(): void {
  closedByUs = false;
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;

  const ws = new WebSocket(`${wsBase()}/api/hub`);
  socket = ws;

  ws.onopen = () => {
    ws.send(JSON.stringify({ t: 'hello', role: 'client', deviceId: getDeviceId(), deviceName: 'Web browser' }));
  };
  ws.onmessage = (ev) => {
    let m: Record<string, unknown>;
    try {
      m = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
    } catch {
      return;
    }
    switch (m.t) {
      case 'presence':
        presence = (m.devices as HubDevice[]) ?? [];
        emitPresence();
        break;
      case 'begin':
        pending.get(String(m.reqId))?.onBegin?.(m as { size?: number; mime?: string });
        break;
      case 'chunk': {
        const p = pending.get(String(m.reqId));
        if (p) p.onChunk(b64ToBytes(String(m.data ?? '')), m.last === true);
        break;
      }
      case 'error':
        pending.get(String(m.reqId))?.onError(String(m.message ?? 'Transfer failed'));
        break;
    }
  };
  ws.onclose = () => {
    if (socket === ws) socket = null;
    presence = [];
    emitPresence();
    if (!closedByUs) setTimeout(connectHub, 2000); // auto-reconnect
  };
  ws.onerror = () => ws.close();
}

export function disconnectHub(): void {
  closedByUs = true;
  socket?.close();
  socket = null;
}

export function isConnected(): boolean {
  return !!socket && socket.readyState === WebSocket.OPEN;
}

/** Download a book by streaming it through the hub from the owning device. */
export function downloadBook(
  deviceId: string,
  book: HubBook,
  onProgress?: (fraction: number) => void,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      reject(new Error('Not connected to the hub.'));
      return;
    }
    const reqId = crypto.randomUUID();
    const chunks: Uint8Array[] = [];
    let total = book.size ?? 0;
    let received = 0;
    let mime = book.type === 'PDF' ? 'application/pdf' : 'application/epub+zip';

    const timeout = setTimeout(() => {
      if (pending.delete(reqId)) reject(new Error('Transfer timed out.'));
    }, 180_000);

    pending.set(reqId, {
      onBegin: (m) => {
        if (typeof m.size === 'number') total = m.size;
        if (m.mime) mime = m.mime;
      },
      onChunk: (bytes, last) => {
        chunks.push(bytes);
        received += bytes.length;
        onProgress?.(total ? Math.min(1, received / total) : 0);
        if (last) {
          clearTimeout(timeout);
          pending.delete(reqId);
          resolve(new Blob(chunks as BlobPart[], { type: mime }));
        }
      },
      onError: (message) => {
        clearTimeout(timeout);
        pending.delete(reqId);
        reject(new Error(message));
      },
    });

    socket.send(JSON.stringify({ t: 'request', reqId, deviceId, bookId: book.id }));
  });
}
